import React, { useMemo, useState } from 'react';
import '../styles/SistemaNotas.css';
import { alunos, turmas } from '../data/escolaData.js';

const SistemaNotas = () => {
  const [turmaSelecionada, setTurmaSelecionada] = useState('');
  const [alunoSelecionado, setAlunoSelecionado] = useState('');
  const [tipoAvaliacao, setTipoAvaliacao] = useState('');
  const [nota, setNota] = useState('');
  const [notasLancadas, setNotasLancadas] = useState([]);

  const turmaOptions = turmas;

  const alunosFiltrados = useMemo(() => {
    if (!turmaSelecionada) return alunos;
    return alunos.filter((aluno) => aluno.turmaId === turmaSelecionada);
  }, [turmaSelecionada]);

  const tiposAvaliacao = [
    { id: 'prova', nome: 'Prova' },
    { id: 'trabalho', nome: 'Trabalho' },
    { id: 'participacao', nome: 'Participação' },
    { id: 'simulado', nome: 'Simulado' },
    { id: 'atividade', nome: 'Atividade' }
  ];

  const arredondarNota = (valor) => {
    const num = parseFloat(valor);
    if (isNaN(num) || num < 0) return 0;
    if (num > 10) return 10;
    
    // Arredonda para o próximo múltiplo de 0.5
    const arredondado = Math.ceil(num * 2) / 2;
    return arredondado;
  };

  const handleNotaChange = (e) => {
    const valor = e.target.value;
    setNota(valor);
  };

  const handleLancarNota = () => {
    if (!turmaSelecionada || !alunoSelecionado || !tipoAvaliacao || !nota) {
      alert('Por favor, preencha todos os campos!');
      return;
    }

    const notaArredondada = arredondarNota(nota);
    
    const novaNota = {
      id: Date.now(),
      turma: turmaOptions.find(t => t.id === turmaSelecionada)?.nome,
      aluno: alunos.find(aluno => aluno.id === alunoSelecionado)?.nome,
      avaliacao: tiposAvaliacao.find(t => t.id === tipoAvaliacao)?.nome,
      notaOriginal: parseFloat(nota),
      notaFinal: notaArredondada,
      data: new Date().toLocaleString('pt-BR')
    };

    setNotasLancadas([...notasLancadas, novaNota]);
    
    // Limpa o formulário
    setNota('');
    setAlunoSelecionado('');
    setTipoAvaliacao('');
    
    alert(`Nota ${notaArredondada} lançada com sucesso!`);
  };

  const handleExcluirNota = (id) => {
    setNotasLancadas(notasLancadas.filter(n => n.id !== id));
  };

  return (
    <div className="sistema-notas">
      <div className="notas-header">
        <h2>Sistema de Notas</h2>
        <p>Gerencie as notas dos alunos de forma eficiente e intuitiva</p>
      </div>

      <div className="notas-form-container">
        <div className="form-section">
          <h3>Lançar Nova Nota</h3>
          
          <div className="form-group">
            <label htmlFor="turma">Turma:</label>
            <select 
              id="turma"
              value={turmaSelecionada}
              onChange={(e) => setTurmaSelecionada(e.target.value)}
              className="form-select"
            >
              <option value="">Selecione uma turma...</option>
              {turmaOptions.map(turma => (
                <option key={turma.id} value={turma.id}>
                  {turma.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="aluno">Aluno:</label>
            <select
              id="aluno"
              value={alunoSelecionado}
              onChange={(e) => setAlunoSelecionado(e.target.value)}
              className="form-select"
            >
              <option value="">Selecione um aluno...</option>
              {alunosFiltrados.map((aluno) => (
                <option key={aluno.id} value={aluno.id}>
                  {aluno.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="avaliacao">Tipo de Avaliação:</label>
            <select 
              id="avaliacao"
              value={tipoAvaliacao}
              onChange={(e) => setTipoAvaliacao(e.target.value)}
              className="form-select"
            >
              <option value="">Selecione o tipo...</option>
              {tiposAvaliacao.map(tipo => (
                <option key={tipo.id} value={tipo.id}>
                  {tipo.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="nota">Nota (0 a 10):</label>
            <input 
              id="nota"
              type="number"
              min="0"
              max="10"
              step="0.1"
              value={nota}
              onChange={handleNotaChange}
              placeholder="Digite a nota..."
              className="form-input"
            />
            {nota && (
              <small className="nota-preview">
                Nota será arredondada para: <strong>{arredondarNota(nota)}</strong>
              </small>
            )}
          </div>

          <button 
            onClick={handleLancarNota}
            className="btn-lancar"
            disabled={!turmaSelecionada || !alunoSelecionado || !tipoAvaliacao || !nota}
          >
            Lançar Nota
          </button>
        </div>
      </div>

      {notasLancadas.length > 0 && (
        <div className="notas-list-container">
          <h3>Notas Lançadas</h3>
          <div className="notas-table">
            <table>
              <thead>
                <tr>
                  <th>Turma</th>
                  <th>Aluno</th>
                  <th>Avaliação</th>
                  <th>Nota Original</th>
                  <th>Nota Final</th>
                  <th>Data</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {notasLancadas.map(notaItem => (
                  <tr key={notaItem.id}>
                    <td>{notaItem.turma}</td>
                    <td>{notaItem.aluno}</td>
                    <td>{notaItem.avaliacao}</td>
                    <td>{notaItem.notaOriginal.toFixed(1)}</td>
                    <td>
                      <span className={`nota-final ${notaItem.notaFinal >= 7 ? 'aprovado' : 'recuperacao'}`}>
                        {notaItem.notaFinal.toFixed(1)}
                      </span>
                    </td>
                    <td>{notaItem.data}</td>
                    <td>
                      <button 
                        onClick={() => handleExcluirNota(notaItem.id)}
                        className="btn-excluir"
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="regras-info">
        <h4>Regras do Sistema:</h4>
        <ul>
          <li>Notas devem estar entre 0 e 10</li>
          <li>Sempre arredondadas para múltiplos de 0.5</li>
          <li>Arredondamento sempre para cima (ex: 7.3 → 7.5, 8.6 → 9.0)</li>
          <li>Notas ≥ 7.0 são consideradas aprovadas</li>
        </ul>
      </div>
    </div>
  );
};

export default SistemaNotas;
